import type { ComponentType, Dispatch, SetStateAction } from "react";
import { Animated, ScrollView, StyleSheet, View } from "react-native";
import type { useTranslation } from "react-i18next";
import {
  MAX_CYCLE_WEEKS,
  MAX_SESSIONS,
  type Draft,
  type Exercise,
  type UnitsPreference,
} from "@jackedlogs/shared";

import type { ConfirmFn, NotifyFn } from "../../seams";
import type { ExerciseTimerSheetProps, LoggerProfile } from "../../Logger/hostDeps";
import type { AnimationSourceFn } from "../../Logger/ExerciseVisualPager";
import type { useTheme } from "../../Logger/theme";
import { TimelineFocusSection } from "../../Logger/TimelineFocusSection";
import { LoggerModals } from "../../Logger/LoggerModals";
import { IntensityGridView } from "../../IntensityGrid/IntensityGridView";
import { usePrescribeMount } from "./usePrescribeMount";
import { usePrescribePlacement } from "./usePrescribePlacement";
import { usePrescribeTreeView } from "./usePrescribeTreeView";
import { CoveragePanel } from "./CoveragePanel";
import { LoadAxisSelector } from "./LoadAxisSelector";
import { PrescribeNoteEditor } from "./PrescribeNoteEditor";
import { EditorTopBar } from "../../primitives/EditorTopBar";
import { useLanguageChip } from "../useLanguageChip";
import { useKindLabels } from "../useKindLabels";
import { PrescribeHeaderFields } from "./PrescribeHeaderFields";
import { PrescribeViewTabs } from "./PrescribeViewTabs";
import { RosterStrip } from "../../Logger/RosterStrip";
import { EmptyFocusCard } from "../../Logger/EmptyFocusCard";
import type { ComposerPickerProps } from "../types";

export type PrescribeTreeProps = {
  /** The composer's own live Draft (useProgramEditor/useComposer). */
  programDraft: Draft;
  /** The composer's setDraft — the K2.2 adapter's one write path. */
  setDraft: Dispatch<SetStateAction<Draft>>;
  exerciseCatalog: ReadonlyArray<Exercise> | undefined;
  cellUnit: UnitsPreference;
  notify: NotifyFn;
  confirm: ConfirmFn;
  profile: LoggerProfile;
  t: ReturnType<typeof useTranslation>["t"];
  i18n: ReturnType<typeof useTranslation>["i18n"];
  theme: ReturnType<typeof useTheme>;
  /** PE-24 — the tree bypasses ComposerEditLayout's own chrome, so it needs
   *  its own back/save bar. Reuses the shared EditorTopBar primitive, not a
   *  hand-rolled one — same seams ComposerEditLayout wires (onCancel from
   *  ProgramComposerProps, onSave = the composer's own persist handle). */
  onCancel: () => void;
  onSave: () => void;
  /** PE-26 — structural authoring (Antenore's target flow, point 5: «posso
   *  aggiungere e togliere esercizi, giorni e settimane»). These are
   *  `useComposer`'s own handles, already bound to the SAME `setDraft` this
   *  tree writes cells through, so adding a day here and editing a set here
   *  travel one write path. ⛔ Not re-derived locally: the pure transforms
   *  live in shared (addSession/removeSession/setCycleWeeks/removeWeekAt) and
   *  binding them a second time would be the second copy. */
  addDay: (name: string) => void;
  removeDay: (idx: number) => void;
  changeCycleWeeks: (weeks: number) => void;
  removeWeek: (week: number) => void;
  /** PE-26 — the three COMPONENT slots `<LoggerModals>` requires from its
   *  host (LoggerModals.tsx ~98-100). `Picker` is the same host-wired
   *  Add-Exercise mount the classic composer already threads (types.ts) —
   *  ComposerPickerProps is a Pick of the same ExercisePickerProps
   *  LoggerExercisePickerProps widens, so it slots into ExercisePickerMount
   *  as-is. ExerciseTimerSheetMount/EffortScalePicker are the two new
   *  prescribe-mode-only props on ProgramComposerProps. */
  Picker: ComponentType<ComposerPickerProps>;
  ExerciseTimerSheetMount: ComponentType<ExerciseTimerSheetProps>;
  EffortScalePicker: ComponentType<Record<string, never>>;
  /** PE-33 — the host's GIF resolver, straight through to the "?" tip's
   *  <ExerciseVisualPager>. Optional like everywhere else: absent means the
   *  tip shows the muscle map alone, which is what it did until now. */
  animationSource?: AnimationSourceFn;
};

// PE-22 K2 — the mount for the parallel `prescribe` editor tree (PE-24),
// gated in ProgramComposer.tsx alongside (never inside) ComposerEditLayout.
// Mockup §03 order, all read off the composer's live Draft:
//   1. THE CARD — TimelineFocusSection (itself renders RosterStrip at its
//      own top), reading the draft through the K1 adapter (slotDraftToEntry)
//      and writing it back through the K2.2 draft-direct dispatch adapter.
//   2. IntensityGridView — PE-27: the ONE grid. Heat from
//      buildIntensityGridFromDraft(draft), the day×week cursor, AND the
//      focused exercise's placement toggle via the block-placement seam
//      (usePrescribePlacement, K2.3) — a small, bounded reuse of
//      deriveBlocks/isPlaced/togglePlacement, NOT useComposer's stateful
//      block editor. It used to be two stacked grids; see the component.
//   3. CoveragePanel — read-only, hardSetsByMuscleFromDraft(draft, catalog).
export function PrescribeTree(props: PrescribeTreeProps) {
  const {
    programDraft,
    setDraft,
    exerciseCatalog,
    cellUnit,
    theme,
    t,
    profile,
    confirm,
    notify,
    onCancel,
    onSave,
    addDay,
    removeDay,
    changeCycleWeeks,
    removeWeek,
    Picker,
    ExerciseTimerSheetMount,
    EffortScalePicker,
  } = props;
  // The ONE axis-label resolver, shared with LoadAxisSelector below: the chip
  // and the card's axis cell must never name the same axis differently.
  const kindLabels = useKindLabels(cellUnit);
  const {
    focusSectionProps,
    roster,
    selectedDay,
    setSelectedDay,
    selectedWeek,
    setSelectedWeek,
    activeExerciseId,
    getExerciseNameBySlug,
    loadAxis,
    timedChip,
    noteChip,
    entryNote,
    loggerModalsProps,
  } = usePrescribeMount(props);

  const dayWeekLabel = (d: number, w: number) =>
    t("composer.dayWeekCursor", { d: d + 1, w: w + 1, defaultValue: "D{{d}}·W{{w}}" });

  const placementLabel = activeExerciseId
    ? getExerciseNameBySlug(activeExerciseId)
    : "";
  const placement = usePrescribePlacement(
    programDraft,
    setDraft,
    activeExerciseId,
    placementLabel,
  );

  // PE-27 fetta 2 — the card for "focused, but not placed in this week".
  // ⛔ Not a new component: EmptyFocusCard already IS the card that stands in
  // the FocusPanel's slot and carries one button. Only the words and the
  // button's job change.
  const renderUnplaced = () => (
    <EmptyFocusCard
      message={t("composer.prescribeNotThisWeek", {
        exercise: placementLabel,
        cursor: dayWeekLabel(selectedDay, selectedWeek),
        defaultValue: "{{exercise}} is not scheduled on {{cursor}}.",
      })}
      action={
        placement
          ? {
              label: t("composer.prescribePlaceHere", {
                cursor: dayWeekLabel(selectedDay, selectedWeek),
                defaultValue: "Place on {{cursor}}",
              }),
              onPress: () => placement.onToggle(selectedDay, selectedWeek),
            }
          : undefined
      }
      theme={theme}
    />
  );

  const {
    intensityGrid,
    hardSetsByMuscle,
    view,
    setView,
    tabsStickyStyle,
    weekDeck,
    weekCue,
  } = usePrescribeTreeView({
    programDraft,
    exerciseCatalog,
    theme,
    selectedWeek,
    setSelectedWeek,
  });

  // PE-32 — the writing-language chip, the SAME hook the classic composer
  // mounts (ComposerMetaBand). His smoke of `4.36.1-dev`: «io ho l'UI in
  // inglese, ma l'editor mi mostra l'italiano» — the prescribe tree never
  // inherited the control, so there was no way to see WHICH language it was
  // writing, let alone change it.
  const language = useLanguageChip({ theme, draft: programDraft, setDraft });

  return (
    <View style={styles.container}>
      {/* ⭐ PINNED, and only this. Back and Save are the surface's exit and its
          commit: they may never scroll away. Everything else below rides in ONE
          scroller with the card — PE-31b, his smoke of `4.36.0-dev`: «il card
          scrolla dietro tutta la parte del titolo, descrizione e roster, quindi
          non posso fare bene lo smoke … il card va probabilmente messo nello
          stesso container del resto della pagina».
          ⛔ `title` stays empty: the name is an editable field one screen-row
          below, and printing it here too would say it twice. */}
      <EditorTopBar
        theme={theme}
        backLabel={t("program.backToList", { defaultValue: "Back to programs" })}
        onBack={onCancel}
        backIconOnly
        title=""
        center={language.trigger}
        onSave={onSave}
        busy={false}
      />
      {/* PE-32 — the chip's tray, and it hangs OUTSIDE the scroller on
          purpose. Inside it would be a fourth child and shift
          `stickyHeaderIndices`, and it would scroll away from the chip that
          opened it. Out here it stays attached to its trigger and costs the
          card nothing once closed, which is the space rule (PE-27): above the
          card only ONE fixed row survives, and a tray is transient, not a
          row. */}
      {language.tray}
      {/* PE-25 — the tree bypasses ComposerEditLayout, so it must supply its
          own scroll parent like the siblings (ComposerEditLayout.tsx:141):
          without one the content below the card is unreachable and a drag on
          the matrix cell resolves as a press. EditorTopBar stays pinned above.
          ⭐ PE-31b — and it is now the scroll parent of the WHOLE page, header
          fields and roster included. Before, those sat outside it: a fixed
          block three rows tall that the card slid behind, leaving the card a
          slot too short to read (his smoke). ⛔ The tabs are the ONE exception,
          and they earn it by being navigation — `stickyHeaderIndices` pins them
          at index 2, so «un tap su una linguetta è quasi gratis» (his rule from
          PE-27) survives the change instead of costing a scroll to the top. */}
      <ScrollView
        style={styles.body}
        contentContainerStyle={styles.bodyContent}
        keyboardShouldPersistTaps="handled"
        stickyHeaderIndices={[2]}
      >
        {/* [0] — the two editable rows of the locked header. */}
        <PrescribeHeaderFields
          theme={theme}
          name={programDraft.name}
          onChangeName={(v) => setDraft((d) => ({ ...d, name: v }))}
          description={programDraft.description}
          onChangeDescription={(v) => setDraft((d) => ({ ...d, description: v }))}
        />
        {/* [1] — PE-27 fetta 2: THE ROSTER, hoisted out of the card. It answers
            "which exercise", the tabs answer "which surface" and the card's
            swipe answers "which week": one control, one job. It sits above the
            switcher because it belongs to BOTH surfaces — in Matrice the grid
            marks where the focused exercise is placed, so losing the roster
            there would leave the marks unattributed.
            ⛔ PE-31 — THE `D·W` LINE IS GONE from this row. It rode here as a
            bright label to the strip's right, and his smoke called it what it
            was: «un'etichetta … che non ho chiesto, e comunque non è per nulla
            allineata al resto, è molto bruto da vedere». Nothing was lost: the
            DAY is the matrix's own selected row, and the WEEK is a control in
            the card's action bar, where it also serves the screen reader. */}
        <View style={styles.roster}>
          <RosterStrip
            entries={roster.entries}
            inactiveIds={roster.inactiveIds}
            activeEntryId={focusSectionProps.tl.resolvedActiveId}
            onFocus={focusSectionProps.tl.focusRow}
            resolveName={getExerciseNameBySlug}
            groupLabelFor={focusSectionProps.ctx.groupLabelFor}
            expanded={focusSectionProps.tl.rosterExpanded}
            onToggleExpanded={() =>
              focusSectionProps.tl.setRosterExpanded((v: boolean) => !v)
            }
            onAddExercise={() => focusSectionProps.ctx.setPickerOpen(true)}
            t={t}
            theme={theme}
          />
        </View>
        {/* [2] — the STICKY child. ⚠️ It needs an opaque background of its own:
            a sticky row is drawn over the content passing beneath it, and
            without a fill the card's set rows read straight through the tabs. */}
        <View style={tabsStickyStyle}>
          <PrescribeViewTabs theme={theme} value={view} onSelect={setView} />
        </View>
        {view === "editor" && (
          // PE-27 fetta 1 / PE-31 — THE WEEK DECK, and it is a gallery now.
          // ⭐ Antenore, smoke 29/08: «deve essere come lo slider
          // animazione/muscle map, come in una galleria foto, si fa il slide
          // con il dito, non con una frecciolina». The arrows are gone; the
          // card follows the thumb and snaps (useWeekSwipe), and the counter
          // moved into the card's own action bar, where it is also the door a
          // screen reader and a keyboard have onto the week.
          // ⛔ `selectedWeek` stays the single source of truth; the deck moves
          // it, it does not keep a second copy.
          // ⚠️ The clip is not decoration: a card that slides sideways without
          // one draws over the tabs and the coverage panel on its way out.
          <View style={styles.deckClip}>
          <Animated.View
            style={weekDeck.style}
            onLayout={weekDeck.onLayout}
            {...weekDeck.panHandlers}
          >
            <TimelineFocusSection
              {...focusSectionProps}
              // The strip is drawn above the tabs now; without this the card
              // would draw a second one, and two rosters mean two chips
              // claiming the same selection.
              hideRoster
              // PE-27 fetta 2 — the honest empty state. An exercise focused
              // from a dimmed chip is not in this week, so the card says that
              // and offers the ONE gesture that changes it. ⭐ The write is
              // `placement.onToggle`, the same function the hold on a grid
              // cell calls: one way to place, two doors onto it.
              prescribeUnplaced={
                roster.inactiveIds.has(focusSectionProps.tl.resolvedActiveId ?? "")
                  ? renderUnplaced()
                  : undefined
              }
              // PE-22 K3.2 — «Cosa determina il carico», in the card's expanded
              // band area (mockup §01). null while no set is bound, same as the
              // effort meter it sits beside.
              prescribeAxis={
                loadAxis ? (
                  <LoadAxisSelector
                    theme={theme}
                    kind={loadAxis.kind}
                    onSelect={loadAxis.onSelect}
                    cellUnit={cellUnit}
                  />
                ) : undefined
              }
              // PE-34 — the same axis the selector above shows, handed to the
              // card's weight field so the number and the axis can never
              // disagree: one source, two controls.
              prescribeLoad={
                loadAxis
                  ? {
                      kind: loadAxis.kind,
                      cellUnit: cellUnit === "lb" ? "lb" : "kg",
                      // ⛔ useKindLabels, the ONE label resolver — the same one
                      // LoadAxisSelector reads, so the chip and the cell can
                      // never name the axis differently.
                      axisLabel: kindLabels[loadAxis.kind],
                    }
                  : undefined
              }
              // PE-31 — the week cursor, in the card's action bar. ⛔ Only the
              // prescribe mount passes it: the logger card has no week.
              weekCue={weekCue}
              // PE-22 K3.4 — "A tempo": reps⇄time toggle, undefined ⇒ chip hidden.
              onToggleTimed={timedChip?.onToggle}
              timedActive={timedChip?.active}
              // "Nota": the chip's selected state is data-driven (has the row a
              // note?); the editor itself is a slot the chip's local open/close
              // state (owned by FocusPanel, like Burn+'s own bar) reveals.
              noteActive={noteChip?.active}
              // PE-37 — the per-EXERCISE note (💬 in the action bar), the
              // SAME editor component as the per-set one: one text-edit row,
              // two levels. ⛔ Only the write path differs — updateSlot vs
              // updateSetRowNote — and that is the difference that matters.
              prescribeEntryNoteEditor={
                entryNote
                  ? (onClose) => (
                      <PrescribeNoteEditor
                        theme={theme}
                        value={entryNote.text}
                        onChange={entryNote.onChange}
                        placeholder={t("composer.exerciseNotePlaceholder", {
                          defaultValue:
                            "Note for this exercise (shown while logging)",
                        })}
                        onClose={onClose}
                      />
                    )
                  : undefined
              }
              prescribeNoteEditor={
                noteChip
                  ? (onClose) => (
                      <PrescribeNoteEditor
                        theme={theme}
                        value={noteChip.text}
                        onChange={noteChip.onChange}
                        placeholder={t("program.setNotesPlaceholder", {
                          defaultValue: "Per-set note (optional)",
                        })}
                        onClose={onClose}
                      />
                    )
                  : undefined
              }
              />
          </Animated.View>
          </View>
        )}
        {view === "matrice" && (
          // PE-27 — ONE grid. Until 28/08 this rendered a placement matrix
          // stacked on an intensity grid: the same shape twice, transposed
          // against each other, asking the reader to hold both. The fused grid
          // carries the heat as fill, the placement as a ✓ layer and the cursor
          // as a ring, so the three facts share a cell without sharing ink.
          // ⭐ The caps are the spine's own (ProgramSpine.tsx), so the two
          // surfaces cannot disagree about when the ＋ disappears.
          <IntensityGridView
            grid={intensityGrid}
            selected={selectedDay}
            onSelect={setSelectedDay}
            selectedWeek={selectedWeek}
            onSelectCell={(dayIdx, week) => {
              setSelectedWeek(week);
              setSelectedDay(dayIdx);
            }}
            placement={placement}
            dark={theme.name === "dark"}
            onAddDay={
              programDraft.sessions.length < MAX_SESSIONS
                ? () => addDay(`D${programDraft.sessions.length + 1}`)
                : undefined
            }
            onAddWeek={
              programDraft.cycleWeeks < MAX_CYCLE_WEEKS
                ? () => changeCycleWeeks(programDraft.cycleWeeks + 1)
                : undefined
            }
            onRemoveWeek={removeWeek}
            onRemoveDay={removeDay}
            // PE-29 — removal is a HOLD on the week head / day button now, so
            // the grid needs the platform dialog: the gesture has no armed
            // state to take back.
            confirm={confirm}
          />
        )}
        {/* PE-27 — coverage is global: it sits below BOTH tabs, never gated by
            the switch, so weekly muscle coverage stays in view whichever
            surface is open. */}
        <CoveragePanel theme={theme} hardSetsByMuscle={hardSetsByMuscle} />
      </ScrollView>
      {/* PE-26 — the same modal stack the real Logger mounts (LoggerScreen.tsx),
          wired onto usePrescribeMount's draft-direct dispatch instead of a live
          session. Un-mounted until now, which is why the card's ＋/swap/tip/
          group/superset taps were silent on the phone. */}
      <LoggerModals
        {...loggerModalsProps}
        theme={theme}
        t={t}
        ExercisePickerMount={Picker}
        ExerciseTimerSheetMount={ExerciseTimerSheetMount}
        EffortScalePicker={EffortScalePicker}
        profile={profile}
        confirm={confirm}
        notify={notify}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1 },
  // The strip alone now — the D·W cursor that shared this row moved into the
  // card's action bar (PE-31), so there is nothing left to lay out beside it.
  roster: { minWidth: 0 },
  // The lane the week deck slides inside. Without it the card's slide-out is
  // drawn over its neighbours instead of leaving the stage.
  deckClip: { overflow: "hidden" },
  body: { flex: 1 },
  // Clear the gesture bar so CoveragePanel (last item) isn't flush to the edge,
  // mirroring the sibling ComposerEditLayout's detailContent paddingBottom.
  bodyContent: { paddingBottom: 64 },
});
